import { Canvas, FabricImage, Rect, FabricText, filters } from "fabric";
import { PRODUCTS } from "../products";
import { ProductType } from "../types";
import { garmentDataUrl, type Side } from "../garment";
import { useDesignerStore } from "../store";

// If real garment photos are dropped at /designer/tshirt-front.png &
// tshirt-back.png (ideally transparent bg), they're used instead of the vector
// mockup and tinted to the chosen colour. Cached so we don't re-probe 404s.
const realPng: Record<string, boolean> = {};

let canvas: Canvas | null = null;

export function setCanvas(instance: Canvas) {
  canvas = instance;
}

export function getCanvas() {
  return canvas;
}

// Draw (or replace) the t-shirt garment behind the artwork, tinted to `color`.
// The garment is excluded from export/serialization — it's always re-rendered
// from the chosen colour + side, so artwork is stored & tinted independently.
export async function drawGarment(side: Side, color: string) {
  if (!canvas) return;
  canvas.getObjects().forEach((o) => {
    if ((o as any).isGarment) canvas!.remove(o);
  });

  let shirt: FabricImage | undefined;
  // Prefer a real photo if one has been provided.
  if (realPng[side] !== false) {
    try {
      const img = await FabricImage.fromURL(`/designer/tshirt-${side}.png`, { crossOrigin: "anonymous" });
      if (img && (img.width ?? 0) > 0) {
        realPng[side] = true;
        if (color && color.toLowerCase() !== "#ffffff") {
          img.filters = [new filters.BlendColor({ color, mode: "multiply" })];
          img.applyFilters();
        }
        shirt = img;
      }
    } catch {
      realPng[side] = false;
    }
  }
  // Otherwise the tintable vector mockup.
  if (!shirt) shirt = await FabricImage.fromURL(garmentDataUrl(color, side));

  shirt.set({
    originX: "center",
    originY: "center",
    left: canvas.getWidth() / 2,
    top: canvas.getHeight() / 2,
    selectable: false,
    evented: false,
    excludeFromExport: true,
  });
  (shirt as any).isGarment = true;
  shirt.scaleToWidth(PRODUCTS.tshirt.width!);
  canvas.add(shirt);
  canvas.sendObjectToBack(shirt);
  canvas.requestRenderAll();
}

// Recolour the current t-shirt without disturbing the artwork.
export async function applyGarmentColor(color: string) {
  const store = useDesignerStore.getState();
  if (store.product !== "tshirt") return;
  await drawGarment(store.side, color);
}

export async function loadProduct(product: ProductType) {
  if (!canvas) return;

  canvas.clear();
  canvas.backgroundColor = "#ffffff";

  const config = PRODUCTS[product];

  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  // -----------------------------
  // Gang sheet (no garment image — a physical 28cm × 100cm sheet)
  // -----------------------------

  if (config.kind === "gangsheet") {
    const margin = 24;
    const sheetHeight = canvasHeight - margin * 2;
    const ratio = config.sizeMm.width / config.sizeMm.height; // 0.28
    const sheetWidth = sheetHeight * ratio;

    const sheet = new Rect({
      originX: "center",
      originY: "top",
      left: canvasWidth / 2,
      top: margin,
      width: sheetWidth,
      height: sheetHeight,
      fill: "#ffffff",
      stroke: "#cbd5e1", // neutral light-gray edge, not blue
      strokeWidth: 1,
      selectable: false,
      evented: false,
      excludeFromExport: true,
    });
    canvas.add(sheet);

    const label = new FabricText(
      `Gang Sheet · ${config.sizeMm.width / 10} × ${config.sizeMm.height / 10} cm`,
      {
        originX: "center",
        left: canvasWidth / 2,
        top: 2,
        fontSize: 14,
        fontFamily: "sans-serif",
        fill: "#64748b",
        selectable: false,
        evented: false,
        excludeFromExport: true,
      }
    );
    canvas.add(label);
    canvas.renderAll();
    return;
  }

  // -----------------------------
  // Shirt — tintable vector garment (front/back), coloured per the store.
  // -----------------------------

  const store = useDesignerStore.getState();
  await drawGarment(store.side, store.garmentColor);
  canvas.renderAll();
}